import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { NS, ORG_SETTINGS_KEYS } from '../../../i18n/constants/keys';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { AdvancedSettings } from './Containers';
import { GeneralSettings } from './SettingsSections/GeneralSettings';
import OrganizationHierarchy from './SettingsSections/OrganizationHierarchy';
import { Subscription } from './SettingsSections/Subscription';
import { SecuritySettings } from './SettingsSections/SecuritySettings';

export const OrganizationSettings = () => {
  const { t } = useTranslation(NS.organizationSettings);
  const [activeTab, setActiveTab] = useState<number>(1); // Default to General Settings
  const tabTitles = [
    { labelKey: ORG_SETTINGS_KEYS.tabs.hierarchy, id: 0 },
    { labelKey: ORG_SETTINGS_KEYS.tabs.general, id: 1 },
    { labelKey: ORG_SETTINGS_KEYS.tabs.subscription, id: 2 },
    { label: 'Security', id: 3 },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <OrganizationHierarchy />;
      case 1:
        return <GeneralSettings />;
      case 2:
        return <Subscription />;
      case 3:
        return <SecuritySettings />;
      case 4:
        return <AdvancedSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1280, mx: 'auto' }}>
      <SettingsSubNav currentPath='/organization-settings' />

      {/* Segmented Tab Control */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            p: 0.5,
            bgcolor: 'rgba(241, 245, 249, 0.9)',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {tabTitles.map(({ labelKey, label, id }: { labelKey?: string; label?: string; id: number }) => {
            const isActive = activeTab === id;
            return (
              <Box
                component='button'
                key={labelKey || label}
                onClick={() => setActiveTab(id)}
                sx={{
                  position: 'relative',
                  px: 2,
                  py: 1,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  borderRadius: '12px',
                  border: 'none',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: isActive ? 'text.primary' : 'text.secondary',
                  transition: 'color 0.2s',
                  zIndex: 1,
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                {isActive && (
                  <Box
                    component={motion.div}
                    layoutId='active-settings-tab-pill'
                    transition={{
                      type: 'spring',
                      damping: 26,
                      stiffness: 350,
                    }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#ffffff',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                      zIndex: -1,
                    }}
                  />
                )}
                <Typography component='span' sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                  {labelKey ? t(labelKey) : label}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      </Box>

      {/* Tab Contents with Smooth Fade & Motion */}
      <AnimatePresence exitBeforeEnter>
        <Box
          component={motion.div}
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          sx={{ width: '100%' }}
        >
          {renderTabContent()}
        </Box>
      </AnimatePresence>
    </Box>
  );
};
